import useShouldUseRewardsBanner from '@/hooks/biz/welcomepack/useShouldUseRewardsBanner';
import uiState from '@/state/ui';
import useTheme from '@/theme/useTheme';
import { getStaticAsset } from '@/utils/staticAsset';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import React, { CSSProperties } from 'react';
import LocalizedLink from '../../ui/LocalizedLink';
import { LogoBox } from './styles';
import { isFomoBoxGame } from '@/utils/checkRuntime';
import useResponsiveUI from '@/hooks/useResponsiveUI';

export const moonclubLogos = {
  mobileLogo: {
    src: getStaticAsset('/assets/images/logo/Rocket.svg'),
    width: 100,
    height: 100,
  },
  desktopLogo: {
    src: getStaticAsset('/assets/images/logo/Rocket.svg'),
    width: 100,
    height: 100,
  },
  textOnlyLogo: {
    src: getStaticAsset('/assets/images/logo/moonclub.svg'),
    width: 100,
    height: 100,
  },
  combinedLogo: {
    src: getStaticAsset('/assets/images/logo/moonclub.svg'),
    width: 100,
    height: 100,
  },
};

export const fomoBoxLogos = {
  mobileLogo: {
    src: getStaticAsset('/assets/images/fomo-box/box-3d.png'),
    width: 100,
    height: 100,
  },
  desktopLogo: {
    src: getStaticAsset('/assets/images/fomo-box/box-3d.png'),
    width: 100,
    height: 100,
  },
  textOnlyLogo: {
    src: getStaticAsset('/assets/images/fomo-box/box.svg'),
    width: 100,
    height: 100,
  },
  combinedLogo: {
    src: getStaticAsset('/assets/images/fomo-box/box-3dx.svg'),
    width: 100,
    height: 100,
  },
};

export const wowSpaceLogos = {
  mobileLogo: {
    src: getStaticAsset('/assets/images/logo/Wow_Space_logo_m.png'),
    width: 64,
    height: 64,
  },
  desktopLogo: {
    src: getStaticAsset('/assets/images/logo/Wow_Space_logo_pc.png'),
    width: 356,
    height: 120,
  },
  textOnlyLogo: {
    src: getStaticAsset('/assets/images/logo/Wow_Space_logo_pc.png'),
    width: 356,
    height: 120,
  },
  combinedLogo: {
    src: getStaticAsset('/assets/images/logo/Wow_Space_logo_pc.png'),
    width: 356,
    height: 120,
  },
};

export const currentAppLogos = isFomoBoxGame ? wowSpaceLogos : moonclubLogos;

export interface MoonLogoProps {
  width?: number;
  height?: number;
  onClick?: () => void;
  preventBrightnessChange?: boolean;
  applyColorFilter?: boolean;
  sx?: CSSProperties;
  variant?: 'default' | 'header' | 'compact';
}

export const MoonLogo: React.FC<MoonLogoProps> = (props) => {
  const { isMobile } = useResponsiveUI();
  const {
    width,
    height,
    onClick,
    preventBrightnessChange = false,
    applyColorFilter = false,
    sx,
    variant = 'default',
  } = props;
  const { theme } = useTheme();

  const { isHeaderTransparent } = useAtomValue(uiState);

  const shouldShowRewardsBanner = useShouldUseRewardsBanner();

  const brightnessFilter =
    applyColorFilter &&
    isHeaderTransparent &&
    shouldShowRewardsBanner &&
    !preventBrightnessChange
      ? 'brightness(0)'
      : 'brightness(1)';

  // 根据 variant 计算尺寸
  const getLogoSize = () => {
    if (width && height) {
      return { width, height };
    }

    const baseLogo = isMobile
      ? currentAppLogos.mobileLogo
      : currentAppLogos.desktopLogo;

    switch (variant) {
      case 'header':
        // 适合导航栏的尺寸，最大高度 40px
        if (isFomoBoxGame) {
          // WoW Space logo 需要保持宽高比
          const aspectRatio = baseLogo.width / baseLogo.height;
          return { width: Math.round(40 * aspectRatio), height: 40 };
        } else {
          // moonclub logo 是正方形，可以直接设置
          return { width: 40, height: 40 };
        }
      case 'compact':
        // 更小的尺寸，适合紧凑布局
        return { width: 24, height: 24 };
      default:
        // 默认使用原始尺寸
        return { width: baseLogo.width, height: baseLogo.height };
    }
  };

  const logoSize = getLogoSize();

  return (
    <LocalizedLink
      href={'/'}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <LogoBox
        onClick={onClick}
        sx={{
          filter: brightnessFilter,
          width: logoSize.width,
          height: logoSize.height,
          ...sx,
        }}
      >
        <Image
          style={{
            width: logoSize.width,
            height: logoSize.height,
          }}
          alt="logo"
          src={
            (isMobile
              ? currentAppLogos.mobileLogo
              : currentAppLogos.desktopLogo
            ).src
          }
          width={logoSize.width}
          height={logoSize.height}
        />
      </LogoBox>
    </LocalizedLink>
  );
};

export default MoonLogo;
